import { useEffect, useState } from "react";
import { Quote, ChevronDown, ChevronUp, Minimize2, Maximize2 } from "lucide-react";
import useIsMobile from "@/hooks/useIsMobile";

/**
 * Presenter side panel — adapts to mobile (bottom sheet) vs desktop (side rail).
 * Auto-collapses body on slide change for mobile to keep the visual prominent.
 */
export default function PresentationPanel({ slide, index, total }) {
  const isMobile = useIsMobile(768);
  const [collapsedDesktop, setCollapsedDesktop] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState(false);

  // Auto-collapse body on each slide change on mobile so user sees viz first
  useEffect(() => {
    if (isMobile) setExpandedMobile(false);
  }, [slide.id, isMobile]);

  if (isMobile) {
    return (
      <aside
        className="fixed left-3 right-3 bottom-[5.6rem] z-40 glass-strong rounded-2xl panel-in-mobile overflow-hidden"
        style={{
          maxHeight: expandedMobile ? "60vh" : "auto",
          boxShadow:
            "0 16px 40px rgba(0,0,0,0.55), 0 0 32px rgba(70,144,217,0.08), inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
        data-testid="presentation-panel"
      >
        <button
          type="button"
          onClick={() => setExpandedMobile((e) => !e)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3"
          data-testid="mobile-panel-toggle"
        >
          <div className="text-left min-w-0">
            <div className="text-[9px] tracking-[0.36em] font-mono uppercase text-white/45">
              Slide {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · {slide.eyebrow}
            </div>
            <div
              className="font-heading text-base font-medium text-white truncate"
              data-testid="slide-title"
            >
              {slide.title}
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-1.5 text-white/55">
            <span className="text-[9px] tracking-[0.22em] uppercase font-mono">
              {expandedMobile ? "Less" : "More"}
            </span>
            {expandedMobile ? (
              <ChevronDown className="w-4 h-4" strokeWidth={1.8} />
            ) : (
              <ChevronUp className="w-4 h-4" strokeWidth={1.8} />
            )}
          </div>
        </button>

        {expandedMobile && (
          <div className="px-4 pb-4 overflow-y-auto max-h-[48vh]">
            <ul className="space-y-2 mb-4" data-testid="slide-body">
              {slide.body.map((line, i) => (
                <li
                  key={i}
                  className="flex gap-2 text-[12px] leading-relaxed text-white/75 font-body"
                >
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-[#4690D9] shrink-0 shadow-[0_0_6px_rgba(70,144,217,0.8)]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <div
              className="relative rounded-xl px-3.5 py-3 border-l-2"
              style={{
                background:
                  "linear-gradient(135deg, rgba(0,59,141,0.10), rgba(70,144,217,0.06))",
                borderLeftColor: "rgba(70,144,217,0.7)",
              }}
            >
              <Quote
                className="absolute -top-2 -left-2 w-3.5 h-3.5 text-[#9CC6F0]/70 bg-[#0F2442] rounded-full p-0.5"
                strokeWidth={2}
              />
              <div className="text-[9px] tracking-[0.32em] font-mono uppercase text-[#9CC6F0]/70 mb-1">
                Presenter Note
              </div>
              <p
                className="text-[11px] italic leading-relaxed text-white/80 font-body"
                data-testid="presenter-note"
              >
                {slide.quote}
              </p>
            </div>
          </div>
        )}
      </aside>
    );
  }

  // ============ DESKTOP ============
  if (collapsedDesktop) {
    return (
      <button
        type="button"
        onClick={() => setCollapsedDesktop(false)}
        className="fixed top-1/2 -translate-y-1/2 right-3 sm:right-5 z-40 glass-strong rounded-l-full pl-3 pr-2 py-3 flex items-center gap-2 hover:bg-white/10 transition-all"
        data-testid="expand-panel-button"
      >
        <Maximize2 className="w-3.5 h-3.5 text-white/70" strokeWidth={1.6} />
        <span className="text-[10px] tracking-[0.32em] uppercase font-mono text-white/70">
          Panel
        </span>
      </button>
    );
  }

  return (
    <aside
      key={slide.id}
      className="fixed top-1/2 -translate-y-1/2 right-6 z-40 w-[360px] max-h-[80vh] overflow-y-auto glass-strong rounded-2xl p-6 panel-in"
      style={{
        boxShadow:
          "0 20px 60px rgba(0,0,0,0.6), 0 0 60px rgba(70,144,217,0.10), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
      data-testid="presentation-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.4em] font-mono uppercase text-white/45">
          Slide {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
        <button
          type="button"
          onClick={() => setCollapsedDesktop(true)}
          className="text-white/45 hover:text-white/85 transition"
          data-testid="collapse-panel-button"
          aria-label="Collapse panel"
        >
          <Minimize2 className="w-3.5 h-3.5" strokeWidth={1.6} />
        </button>
      </div>

      <div className="text-[11px] tracking-[0.32em] font-mono uppercase text-white/55 mb-2">
        {slide.eyebrow}
      </div>
      <h2
        className="font-heading text-3xl font-light tracking-tight text-white mb-4"
        data-testid="slide-title"
      >
        {slide.title}
      </h2>

      <ul className="space-y-2.5 mb-5" data-testid="slide-body">
        {slide.body.map((line, i) => (
          <li
            key={i}
            className="flex gap-2.5 text-[13px] leading-relaxed text-white/75 font-body"
          >
            <span className="mt-1.5 w-1 h-1 rounded-full bg-[#4690D9] shrink-0 shadow-[0_0_6px_rgba(70,144,217,0.8)]" />
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <div
        className="relative rounded-xl px-4 py-3 border-l-2"
        style={{
          background:
            "linear-gradient(135deg, rgba(0,59,141,0.10), rgba(70,144,217,0.06))",
          borderLeftColor: "rgba(70,144,217,0.7)",
        }}
      >
        <Quote
          className="absolute -top-2 -left-2 w-4 h-4 text-[#9CC6F0]/70 bg-[#0F2442] rounded-full p-0.5"
          strokeWidth={2}
        />
        <div className="text-[10px] tracking-[0.32em] font-mono uppercase text-[#9CC6F0]/70 mb-1.5">
          Presenter Note
        </div>
        <p
          className="text-[13px] italic leading-relaxed text-white/80 font-body"
          data-testid="presenter-note"
        >
          {slide.quote}
        </p>
      </div>
    </aside>
  );
}
